import { keyframes, styled } from "goober";
import {
  type ReactNode,
  useId,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { formatHoverTime, formatTimestamp } from "../../lib/formatTimestamp";
import { useChatHistoryScroll } from "../../hooks/useChatHistory";
import { useCursorChatHistory } from "../../hooks/useCursorChatHistory";
import { ClickButton } from "../ClickButton";
import { DragIndicator } from "../DragIndicator";
import { StatusBadge } from "../StatusBadge";
import type { ChatTool } from "./ChatTool";
import { Messages } from "./chatStyles";
import type { PreviousConversation } from "./chatTypes";
import { PreviousTranscript } from "./PreviousTranscript";
import { HistoryLoading } from "./HistoryLoading";
import {
  captureLiveHistoryPosition,
  startLiveHistoryReturn,
} from "./liveHistoryReturn";
import { startLiveHistoryTransition } from "./liveHistoryTransition";
import {
  PEEK_EDGE_HEIGHT,
  PREVIOUS_CHAT_BOTTOM_GAP,
  PREVIOUS_CHAT_OUTLINE_WIDTH,
  usePreviousChat,
} from "./usePreviousChat";

const DEPART_TIMING = "620ms cubic-bezier(0.22, 0.8, 0.2, 1) both";
// The composer overlaps the feed by 4px; the 24px jump button needs 12px above and below.
const LIVE_CHAT_BOTTOM_GAP = 52;

const depart = keyframes`
  from {
    background-color: var(--surface-primary);
    transform: translate3d(0, calc(100% - ${PEEK_EDGE_HEIGHT}px), 0);
  }
  to {
    background-color: var(--surface-secondary);
    transform: translate3d(0, 0, 0);
  }
`;

const tintIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const tintOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;

const Viewport = styled("div")`
  --previous-chat-edge-height: ${PEEK_EDGE_HEIGHT}px;
  height: 100%;
  position: relative;
  &[data-has-previous="true"] {
    overflow: clip;
    margin-inline: calc(-1 * var(--workflow-chat-gutter, 12px));
  }
  &[data-outer-scroll="true"] {
    height: auto;
  }
`;

const Stack = styled("div")`
  height: 100%;
  position: relative;
  isolation: isolate;
  &[data-outer-scroll="true"] {
    height: auto;
  }
`;

const PreviousSurface = styled("div")`
  --previous-chat-inset: 12px;
  position: absolute;
  z-index: 2;
  bottom: calc(100% - ${PEEK_EDGE_HEIGHT}px);
  left: var(--previous-chat-inset);
  right: var(--previous-chat-inset);
  height: calc(100% - ${PREVIOUS_CHAT_BOTTOM_GAP}px);
  display: flex;
  flex-direction: column;
  border-radius: 0 0 16px 16px;
  outline: ${PREVIOUS_CHAT_OUTLINE_WIDTH}px solid var(--border-alpha);
  background: var(--surface-secondary);
  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    border-radius: inherit;
    background: var(--alpha4);
    pointer-events: none;
  }
  transform: translate3d(
    0,
    calc(
      var(--conversation-peek, 0px) - var(--previous-chat-scroll-offset, 0px)
    ),
    0
  );
  &[data-departing="true"] {
    height: calc(100% + ${PEEK_EDGE_HEIGHT}px);
    animation: ${depart} ${DEPART_TIMING};
    pointer-events: none;
    &::before {
      animation: ${tintIn} ${DEPART_TIMING};
    }
  }
  &[data-returning="true"]::before {
    animation: ${tintOut} ${DEPART_TIMING};
  }
  &[data-flowing="true"] {
    position: relative;
    inset: auto;
    height: auto;
    min-height: 0;
    flex: 1;
    transform: translateY(-${PREVIOUS_CHAT_OUTLINE_WIDTH}px);
  }
  @media (prefers-reduced-motion: reduce) {
    &[data-departing="true"],
    &[data-departing="true"]::before {
      animation-duration: 1ms;
    }
  }
`;

const FlowingLiveStack = styled("div")`
  position: sticky;
  top: calc(
    12px + var(--exploratory-banner-height, 36px) -
      var(--live-history-height, 0px)
  );
  z-index: 2;
  height: var(--live-history-height, 0px);
  flex-shrink: 0;
  margin-inline: calc(12px - var(--live-chat-inset));
  /* The first date marker adds 24px, giving a 48px gap below the banner. */
  margin-bottom: 24px;
  &[data-returning="true"] {
    pointer-events: none;
  }
`;

const FlowingLiveContent = styled("div")`
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  > :first-child {
    margin-top: calc(-1 * var(--live-history-extra-height, 0px));
  }
  > :last-child {
    flex-shrink: 0;
  }
`;

const PreviousMessages = styled(Messages)`
  flex: 1;
  min-height: 0;
  margin-right: 0;
  padding-inline: calc(
    12px + var(--workflow-chat-gutter, 12px) - var(--previous-chat-inset)
  );
  overscroll-behavior: contain;
  &[data-flowing="true"] {
    overflow-y: hidden;
    overscroll-behavior: auto;
    min-height: 0;
    &[data-scrollable="true"] {
      overflow-y: auto;
    }
  }
`;

const ActiveSurface = styled("div")`
  position: relative;
  height: 100%;
  &[data-has-previous="true"] {
    margin-inline: var(--workflow-chat-gutter, 12px);
  }
  &[data-outer-scroll="true"] {
    height: auto;
  }
`;

const previousFooterStyle = `
  position: relative;
  flex-shrink: 0;
  width: 100%;
  height: ${PEEK_EDGE_HEIGHT}px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 32px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  padding: 0 12px;
  border-radius: inherit;
  color: var(--text-secondary);
  > :first-child {
    justify-self: start;
  }
  > :last-child {
    justify-self: end;
  }
  > span {
    min-width: 0;
    max-width: 100%;
  }
  > span > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &::before {
    content: "";
    position: absolute;
    top: -0.5px;
    left: 0;
    right: 0;
    height: 0.5px;
    background: transparent;
    pointer-events: none;
  }
  &[data-scrolled="true"]::before {
    background: var(--border-alpha);
  }
`;

const LiveFooter = styled("div")`
  ${previousFooterStyle}
`;

const PeekEdge = styled(ClickButton)`
  ${previousFooterStyle}
  appearance: none;
  margin: 0;
  border: 0;
  box-shadow: none;
  font: inherit;
  background: transparent;
  cursor: grab;
  touch-action: none;
  user-select: none;
  > [data-drag-indicator] {
    justify-self: center;
    height: 8px;
    background: transparent;
  }
  [data-drag-indicator] svg {
    display: block;
  }
  [data-drag-indicator] path {
    d: path("M 1 4 L 16 4 L 31 4");
    stroke: var(--border-alpha);
    transition:
      d 180ms cubic-bezier(0.4, 0, 0.2, 1),
      stroke 120ms ease;
  }
  &:hover [data-drag-indicator] path,
  &:active [data-drag-indicator] path,
  &:focus-visible [data-drag-indicator] path {
    stroke: var(--alpha16);
  }
  &:not([data-dragging="true"]) {
    &:hover [data-drag-indicator] path,
    &:active [data-drag-indicator] path,
    &:focus-visible [data-drag-indicator] path {
      d: path("M 9 2 L 16 6 L 23 2");
    }
    &[aria-expanded="true"]:hover [data-drag-indicator] path,
    &[aria-expanded="true"]:active [data-drag-indicator] path,
    &[aria-expanded="true"]:focus-visible [data-drag-indicator] path {
      d: path("M 9 6 L 16 2 L 23 6");
    }
  }
  @media (prefers-reduced-motion: reduce) {
    [data-drag-indicator] path {
      transition: none;
    }
  }
  &:active {
    cursor: grabbing;
  }
  &:hover {
    color: var(--text-primary);
  }
  &:focus-visible {
    outline: 2px solid var(--control-bg);
    outline-offset: -3px;
  }
`;

function PreviousChatDetails({
  label,
  timestamp,
  children,
}: {
  label: string;
  timestamp: number | undefined;
  children?: ReactNode;
}) {
  return (
    <>
      <StatusBadge color="gray">{label}</StatusBadge>
      {children ?? <span />}
      {timestamp !== undefined ? (
        <StatusBadge color="gray">
          <time
            dateTime={new Date(timestamp).toISOString()}
            title={`Last activity: ${formatTimestamp(timestamp)}`}
          >
            Last activity · {formatHoverTime(timestamp)}
          </time>
        </StatusBadge>
      ) : (
        <span />
      )}
    </>
  );
}

export function ConversationViewport({
  previous,
  departing,
  outerScroll,
  toolsByName,
  currentUserId,
  currentScrollRef,
  children,
}: {
  previous?: PreviousConversation;
  departing: boolean;
  currentScrollRef: React.RefObject<HTMLDivElement | null>;
  outerScroll: boolean;
  toolsByName: Map<string, ChatTool>;
  currentUserId: string | undefined;
  children: React.ReactNode;
}) {
  const regionId = useId();
  const previousTimestamp = previous?.entries.at(-1)?.createdAt;
  const {
    viewportRef,
    stackRef,
    previousScrollRef,
    edgeRef,
    mode,
    atTop,
    isDeparting,
    revealed,
    previousScrolledUp,
    viewportHandlers,
    surfaceHandlers,
    edgeHandlers,
    onPreviousScroll,
  } = usePreviousChat({ previous, departing, currentScrollRef });
  useChatHistoryScroll(
    previousScrollRef,
    previous?.entries ?? [],
    previous?.onLoadOlder,
    previous?.loadingOlder
  );

  return (
    <Viewport
      ref={viewportRef}
      {...viewportHandlers}
      data-has-previous={!!previous}
      data-previous-open={revealed && mode === "open"}
      data-outer-scroll={outerScroll}
    >
      <Stack ref={stackRef} data-outer-scroll={outerScroll}>
        {previous && (
          <PreviousSurface
            {...surfaceHandlers}
            data-departing={isDeparting}
            aria-hidden={!atTop}
            inert={!atTop}
          >
            <PreviousMessages
              id={regionId}
              role="region"
              aria-label={previous.label}
              aria-hidden={!revealed}
              inert={!revealed}
              ref={previousScrollRef}
              onScroll={onPreviousScroll}
              tabIndex={revealed ? 0 : -1}
            >
              {previous.loadingOlder && <HistoryLoading />}
              <PreviousTranscript
                key={previous.id}
                conversation={previous}
                toolsByName={toolsByName}
                currentUserId={currentUserId}
              />
            </PreviousMessages>
            <PeekEdge
              ref={edgeRef}
              {...edgeHandlers}
              type="button"
              data-no-pill
              data-dragging={mode === "dragging"}
              data-scrolled={previousScrolledUp}
              aria-controls={regionId}
              aria-expanded={revealed}
              aria-label={
                revealed
                  ? "Click to return to current chat"
                  : `Click or drag to see ${previous.label.toLowerCase()}`
              }
              disabled={isDeparting}
            >
              <PreviousChatDetails
                label={previous.label}
                timestamp={previousTimestamp}
              >
                <DragIndicator data-drag-indicator aria-hidden="true">
                  <svg width="32" height="8" viewBox="0 0 32 8" fill="none">
                    <path
                      d="M 1 4 L 16 4 L 31 4"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </DragIndicator>
              </PreviousChatDetails>
            </PeekEdge>
          </PreviousSurface>
        )}
        <ActiveSurface
          data-has-previous={!!previous}
          data-outer-scroll={outerScroll}
        >
          {children}
        </ActiveSurface>
      </Stack>
    </Viewport>
  );
}

export function FlowingLiveConversation({
  conversation: sourceConversation,
  detached = false,
  animate,
  toolsByName,
  currentUserId,
  banner,
  onVisibilityChange,
  onCloseComplete,
}: {
  conversation: PreviousConversation;
  detached?: boolean;
  animate: boolean;
  toolsByName: Map<string, ChatTool>;
  currentUserId: string | undefined;
  banner: ReactNode;
  onVisibilityChange: (visible: boolean) => void;
  onCloseComplete?: () => void;
}) {
  const history = useCursorChatHistory(
    sourceConversation.reviewVersion?.commitId ?? "",
    {
      type: "commit",
      commitId: sourceConversation.reviewVersion?.commitId ?? "",
    },
    detached,
    Math.max(1, sourceConversation.entries.length)
  );
  const entries = history.entries;
  const [lastPage, setLastPage] = useState({
    queryEntries: undefined as typeof entries | undefined,
    entries: sourceConversation.entries,
  });
  if (!detached && lastPage.entries !== sourceConversation.entries)
    setLastPage({
      queryEntries: undefined,
      entries: sourceConversation.entries,
    });
  else if (
    detached &&
    history.complete &&
    entries.length > 0 &&
    lastPage.queryEntries !== entries
  )
    setLastPage({ queryEntries: entries, entries });
  const conversation = detached
    ? {
        ...sourceConversation,
        entries: lastPage.entries,
        loadingOlder: history.loadingOlder,
        onLoadOlder: history.onLoadOlder,
      }
    : sourceConversation;
  const stackRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const historyRef = useRef<HTMLDivElement>(null);
  useChatHistoryScroll(
    historyRef,
    conversation.entries,
    conversation.onLoadOlder,
    conversation.loadingOlder
  );
  const bannerRef = useRef<HTMLDivElement>(null);
  const [entering, setEntering] = useState(animate);
  const [atTop, setAtTop] = useState(false);
  const [historyScrolledUp, setHistoryScrolledUp] = useState(false);
  const returning = !!onCloseComplete;
  const finishClose = useEffectEvent(() => onCloseComplete?.());
  const returnPositionRef =
    useRef<ReturnType<typeof captureLiveHistoryPosition>>(undefined);
  const captureReturnPosition = useEffectEvent(() => {
    if (returning && historyRef.current && bannerRef.current)
      returnPositionRef.current = captureLiveHistoryPosition(
        historyRef.current,
        bannerRef.current
      );
  });
  useLayoutEffect(() => {
    if (returning) return;
    const stack = stackRef.current;
    const content = contentRef.current;
    const history = historyRef.current;
    const surface = history?.parentElement;
    const banner = bannerRef.current;
    const scroll = stack?.closest<HTMLDivElement>(
      '[data-current-conversation="true"]'
    );
    if (!stack || !content || !banner || !history || !surface || !scroll)
      return;
    const size = () => {
      stack.style.setProperty(
        "--live-history-height",
        `${Math.max(0, scroll.clientHeight - LIVE_CHAT_BOTTOM_GAP)}px`
      );
      stack.style.setProperty(
        "--exploratory-banner-height",
        `${banner.offsetHeight}px`
      );
    };
    size();
    // The history viewport is shorter than Live once its footer and banner appear.
    history.scrollTop =
      conversation.scrollBottom !== undefined
        ? Math.max(
            0,
            history.scrollHeight -
              history.clientHeight -
              conversation.scrollBottom
          )
        : (conversation.scrollTop ?? history.scrollHeight);
    if (animate) scroll.scrollTop = scroll.scrollHeight;
    let transition: ReturnType<typeof startLiveHistoryTransition> | undefined;
    const onScroll = () => {
      transition?.sync();
      setAtTop(scroll.scrollTop <= 1);
      setHistoryScrolledUp(
        history.scrollHeight -
          history.clientHeight -
          Math.max(0, history.scrollTop) >
          1
      );
    };
    if (animate) {
      transition = startLiveHistoryTransition({
        stack,
        content,
        history,
        viewport: conversation.viewport ?? {
          height: scroll.clientHeight,
          bottom: scroll.getBoundingClientRect().bottom,
        },
        onFinish: () => {
          setEntering(false);
          onScroll();
        },
      });
    }
    onScroll();
    const observer = new ResizeObserver(() => {
      size();
      onScroll();
    });
    observer.observe(scroll);
    if (scroll.firstElementChild) observer.observe(scroll.firstElementChild);
    observer.observe(banner);
    observer.observe(history);
    if (history.firstElementChild) observer.observe(history.firstElementChild);
    // A positive threshold excludes the zero-area intersection at the sticky edge.
    onVisibilityChange(true);
    const visibilityThreshold = 0.001;
    const visibilityObserver = new IntersectionObserver(
      ([entry]) =>
        onVisibilityChange(
          !!entry && entry.intersectionRatio >= visibilityThreshold
        ),
      { root: scroll, threshold: visibilityThreshold }
    );
    visibilityObserver.observe(surface);
    const interrupt = () => {
      transition?.finish();
      setEntering(false);
    };
    const onWheel = (event: WheelEvent) => {
      interrupt();
      if (
        event.defaultPrevented ||
        !event.cancelable ||
        event.ctrlKey ||
        event.shiftKey ||
        event.deltaY === 0 ||
        Math.abs(event.deltaX) > Math.abs(event.deltaY)
      )
        return;
      for (
        let target = event.target instanceof Element ? event.target : null;
        target && target !== scroll;
        target = target.parentElement
      ) {
        if (target === history) continue;
        const remaining =
          event.deltaY < 0
            ? target.scrollTop
            : target.scrollHeight - target.clientHeight - target.scrollTop;
        if (
          remaining > 1 &&
          /auto|scroll/.test(getComputedStyle(target).overflowY)
        )
          return;
      }
      const unit =
        event.deltaMode === 1
          ? 16
          : event.deltaMode === 2
            ? scroll.clientHeight
            : 1;
      const delta = event.deltaY * unit;
      const feedMax = Math.max(0, scroll.scrollHeight - scroll.clientHeight);
      const historyMax = Math.max(
        0,
        history.scrollHeight - history.clientHeight
      );
      const feedTop = Math.max(0, Math.min(feedMax, scroll.scrollTop));
      const historyTop = Math.max(0, Math.min(historyMax, history.scrollTop));
      let nextFeedTop = feedTop;
      let nextHistoryTop = historyTop;
      if (delta < 0) {
        const feedMovement = Math.min(feedTop, -delta);
        nextFeedTop -= feedMovement;
        nextHistoryTop = Math.max(0, historyTop + delta + feedMovement);
      } else if (feedTop <= 1) {
        nextHistoryTop = Math.min(historyMax, historyTop + delta);
        nextFeedTop = Math.min(feedMax, delta - (nextHistoryTop - historyTop));
      } else {
        nextFeedTop = Math.min(feedMax, feedTop + delta);
      }
      if (nextFeedTop === feedTop && nextHistoryTop === historyTop) return;
      // Native scroll latching otherwise requires a new gesture at the Live boundary.
      event.preventDefault();
      scroll.scrollTop = nextFeedTop;
      history.scrollTop = nextHistoryTop;
      onScroll();
    };
    scroll.addEventListener("scroll", onScroll, { passive: true });
    history.addEventListener("scroll", onScroll, { passive: true });
    scroll.addEventListener("wheel", onWheel, { passive: false });
    scroll.addEventListener("touchstart", interrupt, { passive: true });
    return () => {
      observer.disconnect();
      visibilityObserver.disconnect();
      captureReturnPosition();
      transition?.dispose();
      scroll.removeEventListener("scroll", onScroll);
      history.removeEventListener("scroll", onScroll);
      scroll.removeEventListener("wheel", onWheel);
      scroll.removeEventListener("touchstart", interrupt);
    };
  }, [
    conversation.id,
    conversation.scrollTop,
    conversation.scrollBottom,
    conversation.viewport,
    animate,
    onVisibilityChange,
    returning,
  ]);
  useLayoutEffect(() => {
    if (!returning) return;
    const stack = stackRef.current;
    const content = contentRef.current;
    const history = historyRef.current;
    const banner = bannerRef.current;
    const scroll = stack?.closest<HTMLElement>(
      '[data-current-conversation="true"]'
    );
    if (!stack || !content || !history || !banner || !scroll) {
      finishClose();
      return;
    }
    return startLiveHistoryReturn({
      scroll,
      content,
      history,
      banner,
      transcript: stack.nextElementSibling as HTMLElement | null,
      onFinish: finishClose,
      from: returnPositionRef.current,
    });
  }, [returning]);
  return (
    <FlowingLiveStack
      ref={stackRef}
      data-live-history-stack
      data-returning={returning}
    >
      <FlowingLiveContent ref={contentRef}>
        <PreviousSurface data-flowing="true" data-returning={returning}>
          <PreviousMessages
            ref={historyRef}
            data-flowing="true"
            data-scrollable={atTop && !entering}
            role="region"
            aria-label={conversation.label}
            tabIndex={0}
          >
            {conversation.loadingOlder && <HistoryLoading />}
            <PreviousTranscript
              conversation={conversation}
              toolsByName={toolsByName}
              currentUserId={currentUserId}
            />
          </PreviousMessages>
          <LiveFooter data-scrolled={historyScrolledUp}>
            <PreviousChatDetails
              label={conversation.label}
              timestamp={conversation.entries.at(-1)?.createdAt}
            />
          </LiveFooter>
        </PreviousSurface>
        <div ref={bannerRef}>{banner}</div>
      </FlowingLiveContent>
    </FlowingLiveStack>
  );
}
